import {
  collection,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  serverTimestamp,
  setDoc,
  writeBatch,
  Timestamp,
  type Firestore,
} from 'firebase/firestore';
import type { User } from 'firebase/auth';
import { getDb } from './firebase';
import type { OptionOrder } from './types';
import type { QuizStatus } from './quizStorage';

export interface CloudProgress {
  courseSlug: string;
  sectionSlug: string;
  picks: Record<string, OptionOrder>;
  crossed: Record<string, OptionOrder[]>;
  currentTime: number;
  status: QuizStatus;
  showLiveScript: boolean;
  updatedAtMs: number;
}

export function progressDocId(courseSlug: string, sectionSlug: string): string {
  return `${courseSlug}__${sectionSlug}`;
}

function userDoc(db: Firestore, uid: string) {
  return doc(db, 'users', uid);
}

function progressDoc(db: Firestore, uid: string, courseSlug: string, sectionSlug: string) {
  return doc(db, 'users', uid, 'progress', progressDocId(courseSlug, sectionSlug));
}

/**
 * Read the `settings` map saved on users/{uid}. Returns null if user doc or
 * the field doesn't exist.
 */
export async function getCloudSettings(uid: string): Promise<Record<string, unknown> | null> {
  const db = getDb();
  const snap = await getDoc(userDoc(db, uid));
  if (!snap.exists()) return null;
  const data = snap.data();
  const s = data.settings;
  if (!s || typeof s !== 'object') return null;
  return s as Record<string, unknown>;
}

/** Write the settings map to users/{uid}.settings (merged). */
export async function saveCloudSettings(uid: string, settings: Record<string, unknown>): Promise<void> {
  const db = getDb();
  await setDoc(
    userDoc(db, uid),
    { settings, updatedAt: serverTimestamp() },
    { merge: true },
  );
}

export async function ensureUserProfile(user: User): Promise<void> {
  const db = getDb();
  const ref = userDoc(db, user.uid);
  const snap = await getDoc(ref);
  if (!snap.exists()) {
    await setDoc(ref, {
      email: user.email ?? null,
      displayName: user.displayName ?? null,
      photoURL: user.photoURL ?? null,
      isAnonymous: user.isAnonymous,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });
  } else {
    await setDoc(
      ref,
      {
        email: user.email ?? null,
        displayName: user.displayName ?? null,
        photoURL: user.photoURL ?? null,
        isAnonymous: user.isAnonymous,
        updatedAt: serverTimestamp(),
      },
      { merge: true },
    );
  }
}

export async function getCloudProgress(
  uid: string,
  courseSlug: string,
  sectionSlug: string,
): Promise<CloudProgress | null> {
  const db = getDb();
  const snap = await getDoc(progressDoc(db, uid, courseSlug, sectionSlug));
  if (!snap.exists()) return null;
  const data = snap.data();
  const ts = data.updatedAt;
  const updatedAtMs =
    ts instanceof Timestamp ? ts.toMillis() : typeof data.updatedAtMs === 'number' ? data.updatedAtMs : 0;
  return {
    courseSlug: data.courseSlug ?? courseSlug,
    sectionSlug: data.sectionSlug ?? sectionSlug,
    picks: (data.picks ?? {}) as Record<string, OptionOrder>,
    crossed: (data.crossed ?? {}) as Record<string, OptionOrder[]>,
    currentTime: typeof data.currentTime === 'number' ? data.currentTime : 0,
    status: (data.status ?? 'idle') as QuizStatus,
    showLiveScript: !!data.showLiveScript,
    updatedAtMs,
  };
}

export interface SaveCloudProgressInput {
  picks: Record<string, OptionOrder>;
  crossed: Record<string, OptionOrder[]>;
  currentTime: number;
  status: QuizStatus;
  showLiveScript: boolean;
}

export async function saveCloudProgress(
  uid: string,
  courseSlug: string,
  sectionSlug: string,
  patch: SaveCloudProgressInput,
): Promise<void> {
  const db = getDb();
  await setDoc(
    progressDoc(db, uid, courseSlug, sectionSlug),
    {
      courseSlug,
      sectionSlug,
      picks: patch.picks,
      crossed: patch.crossed,
      currentTime: patch.currentTime,
      status: patch.status,
      showLiveScript: patch.showLiveScript,
      updatedAt: serverTimestamp(),
    },
    { merge: true },
  );
}

export async function deleteCloudProgress(
  uid: string,
  courseSlug: string,
  sectionSlug: string,
): Promise<void> {
  const db = getDb();
  await deleteDoc(progressDoc(db, uid, courseSlug, sectionSlug));
}

export async function listProgress(uid: string): Promise<CloudProgress[]> {
  const db = getDb();
  const snap = await getDocs(collection(db, 'users', uid, 'progress'));
  return snap.docs.map((d) => {
    const data = d.data();
    const ts = data.updatedAt;
    const updatedAtMs =
      ts instanceof Timestamp ? ts.toMillis() : typeof data.updatedAtMs === 'number' ? data.updatedAtMs : 0;
    return {
      courseSlug: data.courseSlug,
      sectionSlug: data.sectionSlug,
      picks: (data.picks ?? {}) as Record<string, OptionOrder>,
      crossed: (data.crossed ?? {}) as Record<string, OptionOrder[]>,
      currentTime: typeof data.currentTime === 'number' ? data.currentTime : 0,
      status: (data.status ?? 'idle') as QuizStatus,
      showLiveScript: !!data.showLiveScript,
      updatedAtMs,
    };
  });
}

export async function countProgress(uid: string): Promise<number> {
  const db = getDb();
  const snap = await getDocs(collection(db, 'users', uid, 'progress'));
  return snap.size;
}

export interface LocalProgressInput {
  courseSlug: string;
  sectionSlug: string;
  picks: Record<string, OptionOrder>;
  crossed: Record<string, OptionOrder[]>;
  currentTime: number;
  status: QuizStatus;
  showLiveScript: boolean;
  /** Local updatedAt as JS ms epoch. */
  updatedAtMs: number;
}

export interface MigrationResult {
  inProgressMerged: number;
  attemptsCreated: number;
}

/**
 * Copy locally-stored quiz state into a freshly-signed-in user:
 *  - Local state with status === 'finished' → convert to attempt docs (immutable)
 *  - Local state still in progress → upsert into the live progress collection,
 *    keeping whichever side has the newer updatedAt.
 *
 * Computing correctCount for finished items requires the section's questions,
 * so this resolver is passed in lazily to avoid a hard dep on queries.ts.
 */
export async function migrateLocalProgressTo(
  uid: string,
  local: LocalProgressInput[],
  resolveQuestions: (
    courseSlug: string,
    sectionSlug: string,
  ) => Promise<{ id: string; correct_option_order: OptionOrder }[] | null>,
  createAttemptFn: (uid: string, attempt: {
    courseSlug: string;
    sectionSlug: string;
    picks: Record<string, OptionOrder>;
    crossed: Record<string, OptionOrder[]>;
    correctCount: number;
    totalQuestions: number;
    answeredCount: number;
    durationSeconds: number;
  }) => Promise<string>,
): Promise<MigrationResult> {
  if (local.length === 0) return { inProgressMerged: 0, attemptsCreated: 0 };
  const db = getDb();

  const finished = local.filter((x) => x.status === 'finished');
  const inProgress = local.filter((x) => x.status !== 'finished');

  // 1. Migrate in-progress as live progress docs (skip if cloud already newer)
  let inProgressMerged = 0;
  if (inProgress.length > 0) {
    const cloudList = await listProgress(uid).catch(() => []);
    const cloudIndex = new Map(cloudList.map((p) => [progressDocId(p.courseSlug, p.sectionSlug), p]));
    const batch = writeBatch(db);
    for (const src of inProgress) {
      const key = progressDocId(src.courseSlug, src.sectionSlug);
      const existing = cloudIndex.get(key);
      if (existing && existing.updatedAtMs >= src.updatedAtMs) continue;
      batch.set(progressDoc(db, uid, src.courseSlug, src.sectionSlug), {
        courseSlug: src.courseSlug,
        sectionSlug: src.sectionSlug,
        picks: src.picks,
        crossed: src.crossed,
        currentTime: src.currentTime,
        status: src.status,
        showLiveScript: src.showLiveScript,
        updatedAt: serverTimestamp(),
      });
      inProgressMerged++;
    }
    if (inProgressMerged > 0) await batch.commit();
  }

  // 2. Migrate finished as attempt snapshots.
  let attemptsCreated = 0;
  for (const src of finished) {
    const questions = await resolveQuestions(src.courseSlug, src.sectionSlug).catch(() => null);
    if (!questions) continue;
    const correctCount = questions.filter((q) => src.picks[q.id] === q.correct_option_order).length;
    const totalQuestions = questions.length;
    const answeredCount = Object.keys(src.picks).length;
    try {
      await createAttemptFn(uid, {
        courseSlug: src.courseSlug,
        sectionSlug: src.sectionSlug,
        picks: src.picks,
        crossed: src.crossed,
        correctCount,
        totalQuestions,
        answeredCount,
        durationSeconds: Math.max(0, Math.round(src.currentTime)),
      });
      attemptsCreated++;
    } catch {
      // ignore individual failures
    }
  }

  return { inProgressMerged, attemptsCreated };
}
